import { useTranslation } from "@/hooks/useTranslation";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	Button,
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Input,
} from "@st-marketplace/ui";
import React from "react";
import { useForm } from "react-hook-form";
import {
	type ProxyFormData,
	type ProxyInitialValues,
	getProxySchema,
} from "./proxyForm.schema";

type Props = {
	submitLabel: string;
	initialValues: ProxyInitialValues;
	loading?: boolean;
	disabledFields?: Partial<Record<keyof ProxyInitialValues, boolean>>;
	onSubmit: (data: ProxyFormData) => Promise<void> | void;
	onReset?: () => void;
};

export const ProxyForm: React.FC<Props> = ({
	submitLabel,
	initialValues,
	loading = false,
	disabledFields,
	onSubmit,
	onReset,
}) => {
	const { t } = useTranslation();
	const schema = React.useMemo(() => getProxySchema(t), [t]);

	const form = useForm<ProxyFormData>({
		resolver: zodResolver(schema),
		defaultValues: {
			ip_address: initialValues.ip_address,
			port: initialValues.port,
			username: initialValues.username,
			password: initialValues.password,
		},
	});

	const handleReset = () => {
		form.reset();
		onReset?.();
	};

	const handleSubmit = form.handleSubmit(async (data) => {
		await onSubmit(data);
		handleReset();
	});

	return (
		<Form {...form}>
			<form
				noValidate
				onSubmit={handleSubmit}
				className="grid grid-cols-12 gap-4"
			>
				<div className="col-span-12 md:col-span-7">
					<FormField
						control={form.control}
						name="ip_address"
						render={({ field }) => (
							<FormItem>
								<FormLabel required>{t("proxy.new.formIpAddress")}</FormLabel>
								<FormControl>
									<Input
										{...field}
										ltr
										placeholder={t("proxy.new.formIpAddress")}
										disabled={!!disabledFields?.ip_address}
									/>
								</FormControl>
								<FormMessage />
								<FormDescription>
									{t("validation.urlWithProtocol")}
								</FormDescription>
							</FormItem>
						)}
					/>
				</div>

				<div className="col-span-12 md:col-span-5">
					<FormField
						control={form.control}
						name="port"
						render={({ field }) => (
							<FormItem>
								<FormLabel required>{t("proxy.new.formPort")}</FormLabel>
								<FormControl>
									<Input
										type="number"
										placeholder={t("proxy.new.formPort")}
										value={field.value ?? ""}
										onChange={field.onChange}
										disabled={!!disabledFields?.port}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<div className="col-span-12 md:col-span-4">
					<FormField
						control={form.control}
						name="username"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t("proxy.new.formUsername")}</FormLabel>
								<FormControl>
									<Input
										{...field}
										value={field.value ?? ""}
										ltr
										placeholder={t("proxy.new.formUsername")}
										disabled={!!disabledFields?.username}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<div className="col-span-12 md:col-span-4">
					<FormField
						control={form.control}
						name="password"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t("proxy.new.formPassword")}</FormLabel>
								<FormControl>
									<Input
										{...field}
										value={field.value ?? ""}
										ltr
										placeholder={t("proxy.new.formPassword")}
										disabled={!!disabledFields?.password}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<div className="col-span-6 md:col-span-2 self-end">
					<Button
						type="button"
						color="secondary"
						className="w-full"
						onClick={handleReset}
					>
						{t("proxy.new.formReset")}
					</Button>
				</div>

				<div className="col-span-6 md:col-span-2 self-end">
					<Button
						type="submit"
						color="primary"
						className="w-full"
						loading={loading}
					>
						{submitLabel}
					</Button>
				</div>
			</form>
		</Form>
	);
};
